import type { ReactElement } from "react";
import styles from "./SidetextRow.module.css";

export interface SidetextRowProps {
  label: string;
  /** Muted subline under the primary label (left column). */
  secondaryLabel?: string;
  sideText: string;
  /** Render side text in DM Mono (coordinates, parcel numbers, etc.). */
  monoSideText?: boolean;
  /** Allow side text to wrap with a narrower column (long FEMA descriptions). */
  multiline?: boolean;
  /** Handset drawer sizing — matches drawer nav / checkbox rows. */
  drawerRail?: boolean;
}

/** Label + right-aligned muted side text — shared inspector metric rows. */
export function SidetextRow({
  label,
  secondaryLabel,
  sideText,
  monoSideText = false,
  multiline = false,
  drawerRail = false
}: SidetextRowProps): ReactElement {
  const sideClass = [
    styles.sideText,
    monoSideText ? styles.sideTextMono : null
  ]
    .filter(Boolean)
    .join(" ");

  const rowClass = [
    styles.row,
    secondaryLabel ? styles.rowWithSecondary : null,
    multiline ? styles.rowMultiline : null,
    drawerRail ? styles.rowDrawerRail : null,
    drawerRail && multiline ? styles.rowDrawerRailMultiline : null
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rowClass}>
      <div className={styles.labelCol}>
        <span className={styles.label}>{label}</span>
        {secondaryLabel ? (
          <span className={styles.labelSecondary}>{secondaryLabel}</span>
        ) : null}
      </div>
      <span className={sideClass}>{sideText}</span>
    </div>
  );
}

/** Standalone muted side text (empty states, footnotes). */
export function SidetextSideText({
  children,
  mono = false
}: {
  children: string;
  mono?: boolean;
}): ReactElement {
  const className = [styles.sideText, mono ? styles.sideTextMono : null]
    .filter(Boolean)
    .join(" ");
  return <p className={className}>{children}</p>;
}
